import get from 'lodash/get';
import values from 'lodash/values';
import { all, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { openModal } from '../actions/Modal';
import { openOverlay, openVettedOverlay } from '../actions/Overlay';
import { ApiActions } from '../constants/ApiActions';
import { AppActions, AppConstants } from '../constants/App';
import {
  AccountMessageBlueBars,
  BlueBarButtons,
  microsoftEdgeUrl,
  ReloadMessageStrings,
  unsupportedBrowserStrings,
} from '../constants/BlueBar';
import { BrowserSupportLevels, ClientVersionSemverRegex } from '../constants/ClientInfo';
import {
  AccountMessageBlueBarSubtype,
  BrowserBarSubtype,
  OVERLAY_ACTIONS as actions,
  OVERLAY_CONFIG,
  OVERLAY_TYPES,
  PremiumWelcomeModalSubtype,
} from '../constants/Overlay';
import { getInDomain } from '../domains/externalUtils';
import { isIE } from '../lib/CapabilitiesUtils';
import { shouldPromptUserReload } from '../lib/ClientInfoUtils';
import { isWithinFrequencyCap } from '../lib/Overlay';
import * as Utils from '../utils';

const accountMessageBlueBarValues = values(AccountMessageBlueBars);

export function* onAuth(action) {
  if (action && action.response && action.response.result) {
    const listener = action.response.entities.listeners[action.response.result];
    if (listener && listener.accountMessage) {
      const messageId = listener.accountMessage;
      if (accountMessageBlueBarValues.indexOf(messageId) >= 0) {
        yield put(
          openOverlay({
            [OVERLAY_CONFIG.TYPE]: OVERLAY_TYPES.blueBar,
            [OVERLAY_CONFIG.SUBTYPE]: AccountMessageBlueBarSubtype,
            [OVERLAY_CONFIG.PROPS]: {
              id: messageId,
            },
          }),
        );
      }
    }

    // @todo - WEBCLIENT-3991 - can this block be safely removed?
    const shouldShowPremiumFeatures = yield select(Utils.Listener.hasPremiumFeatures);
    if (
      !action.isReauth &&
      shouldShowPremiumFeatures &&
      get(listener, 'config.branding') === AppConstants.BRANDING_PANDORA_PREMIUM
    ) {
      const hasStations = yield select(getInDomain('playback.utils.listenerHasStations', false));
      const seenWebPremiumWelcome = yield select(Utils.Listener.hasSeenWebPremiumWelcome);
      if (hasStations && !seenWebPremiumWelcome) {
        const premiumWelcomeModelProps = {
          modalType: 'ModalPremiumWelcome',
        };
        yield put(openModal(premiumWelcomeModelProps, null, PremiumWelcomeModalSubtype));
      }
    }

    // listener.webClientVersion may be 'undefined'
    const mostRecentVersion = listener.webClientVersion
      ? listener.webClientVersion.match(ClientVersionSemverRegex)[0]
      : '';
    const getShouldPromptUserReload = yield select(shouldPromptUserReload, mostRecentVersion);
    if (getShouldPromptUserReload) {
      yield put(
        openOverlay({
          [OVERLAY_CONFIG.TYPE]: OVERLAY_TYPES.blueBar,
          [OVERLAY_CONFIG.PROPS]: {
            message: ReloadMessageStrings.reloadNow,
            buttonType: BlueBarButtons.RELOAD,
            buttonRight: true,
          },
        }),
      );
    }
  }
}

export function* checkBrowser({ payload }) {
  if (isIE()) {
    yield put(
      openOverlay({
        [OVERLAY_CONFIG.TYPE]: OVERLAY_TYPES.blueBar,
        [OVERLAY_CONFIG.SUBTYPE]: BrowserBarSubtype,
        [OVERLAY_CONFIG.PROPS]: {
          messageString: unsupportedBrowserStrings.unsupportedIE11MessageString,
          buttonUrl: microsoftEdgeUrl,
          buttonString: unsupportedBrowserStrings.unsupportedIE11ButtonString,
        },
      }),
    );
  }
  if (payload.browserSupportLevel === BrowserSupportLevels.BASIC) {
    yield put(
      openOverlay({
        [OVERLAY_CONFIG.TYPE]: OVERLAY_TYPES.blueBar,
        [OVERLAY_CONFIG.SUBTYPE]: BrowserBarSubtype,
        [OVERLAY_CONFIG.PROPS]: {
          messageString: unsupportedBrowserStrings.unsupportedHeaderMessageString,
          buttonString: unsupportedBrowserStrings.unsupportedHeaderButtonString,
        },
      }),
    );
  }
}

export function* createOverlayIfPassingChecks(action) {
  const shouldCreate = yield select(isWithinFrequencyCap, action.overlayConfig);
  if (shouldCreate) {
    yield put(openVettedOverlay(action.overlayConfig));
  }
}

export default function* overlaySaga() {
  yield all([
    takeLatest(ApiActions.REGISTER_SUCCESS, onAuth),
    takeLatest(ApiActions.LOGIN_SUCCESS, onAuth),
    takeLatest(ApiActions.ANON_LOGIN_SUCCESS, onAuth),
    takeLatest(ApiActions.DEVICE_LOGIN_SUCCESS, onAuth),
    takeLatest(AppActions.INITIALIZE_CLIENT, checkBrowser),
    takeEvery(actions.CHECK_AND_MAYBE_OPEN_OVERLAY, createOverlayIfPassingChecks),
  ]);
}
